import strings from 'owa-strings/owa-mini-common-strings';
import { format } from 'owa-strings';
import { formatDate, getYear, OwaDate, today } from 'owa-datetime';
import { getDayWeekDayFormat } from 'owa-datetime/lib/formatters/formatDayWeekDay';
import { getShortWeekDayFormat } from 'owa-datetime/lib/formatters/formatShortWeekDay';
import { getWeekDayFormat } from 'owa-datetime/lib/formatters/formatWeekDay';

function isCurrentYear(date: OwaDate): boolean {
    return getYear(date) === getYear(today());
}

/**
 * Returns the shorter version of date & month: 29 Sep
 */
export function formatDateMonthShort(date: OwaDate): string {
    return formatDate(date, format(strings.dateWeekDayLong, strings.d, strings.MMM));
}

/**
 * Returns the shorter version of day, date & month: Mon, 4 Oct
 */
export function formatDayDateMonthShort(date: OwaDate): string {
    return formatDate(
        date,
        format(strings.weekDayDateMonthShort, getShortWeekDayFormat(), strings.d, strings.MMM)
    );
}

/**
 * Returns the longer version of day, date & month: Monday, 4 October
 */
export function formatDayDateMonthLong(date: OwaDate): string {
    return formatDate(
        date,
        format(strings.weekDayDateMonthLong, getWeekDayFormat(), strings.d, strings.MMMM)
    );
}

/**
 * Returns the shorter version of day, date, month & year: Mon, 4 Oct 1993
 */
export function formatDayDateMonthYearShort(date: OwaDate): string {
    return formatDate(
        date,
        format(
            strings.weekDayDateMonthYearShort,
            getShortWeekDayFormat(),
            strings.d,
            strings.MMM,
            strings.yyyy
        )
    );
}

/**
 * Returns the longer version of day, date, month & year: Monday, 4 October 1993
 */
export function formatDayDateMonthYearLong(date: OwaDate): string {
    return formatDate(
        date,
        format(
            strings.weekDayDateMonthYearLong,
            getWeekDayFormat(),
            strings.d,
            strings.MMMM,
            strings.yyyy
        )
    );
}

/**
 * Returns short format of given date in this order: date, day, Month, Year. Includes year if the input date is not of current year.
 * @param date input date
 */
export function getDateDayMonthYearShort(date: OwaDate): string {
    return isCurrentYear(date) ? formatDayDateMonthShort(date) : formatDayDateMonthYearShort(date);
}

/**
 * Returns short format of given date in this order: date, day, Month, Year. Includes year if the input date is not of current year.
 * @param date input date
 */
export function getDateMonthYearShort(date: OwaDate): string {
    return isCurrentYear(date) ? formatDayDateMonthShort(date) : formatDayDateMonthYearShort(date);
}

/**
 * Returns long format of given date in this order: date, day, Month, Year. Includes year if the input date is not of current year.
 * @param date input date
 */
export function getDateDayMonthYearLong(date: OwaDate): string {
    return isCurrentYear(date) ? formatDayDateMonthLong(date) : formatDayDateMonthYearLong(date);
}

/**
 * Returns the detailed month and year like April 2019
 */
export function getMonthYearLong(selectedDate: OwaDate): string {
    return formatDate(selectedDate, format(strings.monthYearLong, strings.MMMM, strings.yyyy));
}

/**
 * Returns the detailed date like 5 Wednesday
 */
export function getDateDayLong(selectedDate: OwaDate): string {
    return formatDate(selectedDate, getDayWeekDayFormat());
}
